import { joinPl, pickLine } from "./data/lines.js";
import { statKey } from "./engine.js";
import { activeMute, dueQuests, effectiveMode, matchesQuest, restedPool } from "./rules.js";
import { MINUTE, addDays, dayKey, localMinutes, parseHm, weekKey, zonedParts } from "./time.js";
import {
  AREA_NAMES_PL,
  type Area,
  type DeviceProfile,
  type GameState,
  type NudgeType,
  type OutcomeCounts,
  type PromptKind,
} from "./types.js";

export type ChipAction =
  | { type: "complete_quest"; id: string }
  | { type: "postpone_quest"; id: string }
  | { type: "reminder_ack"; id: string; outcome: "done" | "hour" | "tomorrow" }
  | { type: "rate_week"; value: number }
  | { type: "log_challenge"; area: Area; text: string }
  | { type: "later" }
  | { type: "not_yet" };

export interface Chip {
  label: string;
  action: ChipAction;
}

export interface PromptPlan {
  kind: PromptKind;
  nudgeType: NudgeType | null;
  text: string;
  chips: Chip[];
  /** Open the mic for ~8 s after speaking. */
  listen: boolean;
  /** Speak aloud (false = bubble only). */
  speak: boolean;
  questId: string | null;
  reminderId: string | null;
}

export interface Presence {
  cameraOn: boolean;
  lastMotionAt: number;
  lastTouchAt: number;
}

export interface SchedulerInput {
  state: GameState;
  now: number;
  presence: Presence;
  profile: DeviceProfile;
  /** This device currently hosts the companion. */
  hasCompanion: boolean;
  /** A conversation or prompt is in progress. */
  busy: boolean;
}

export const MOTION_WINDOW = 90_000;
export const TOUCH_WINDOW = 2 * MINUTE;
export const MIN_PROMPT_GAP = 10 * MINUTE;
export const NUDGE_GAP = 2 * 60 * MINUTE;
export const LISTEN_MS = 8_000;
/** A prompt with no reply within this time counts as ignored. */
export const IGNORE_AFTER = 5 * MINUTE;

const BASE_PRIORITY: Record<NudgeType, number> = { chore: 4, quest: 3, challenge: 2, curiosity: 1 };
export const ADAPT_MIN_SAMPLES = 3;
export const ADAPT_SKIP_BELOW = 0.25;

export function isPresent(p: Presence, now: number): boolean {
  return now - p.lastMotionAt < MOTION_WINDOW || now - p.lastTouchAt < TOUCH_WINDOW;
}

/** Brief, debrief, weekly and return greetings: with camera on they wait for presence, without it they just go. */
function canStartScheduled(p: Presence, now: number): boolean {
  return p.cameraOn ? isPresent(p, now) : true;
}

/** Daily nudges need presence; without a camera only shortly after a touch. */
function canNudge(p: Presence, now: number): boolean {
  return p.cameraOn ? isPresent(p, now) : now - p.lastTouchAt < TOUCH_WINDOW;
}

function inWindow(minutes: number, from: string, until: string): boolean {
  const a = parseHm(from) ?? 0;
  const b = parseHm(until) ?? 0;
  return a <= b ? minutes >= a && minutes < b : minutes >= a || minutes < b;
}

/** Adaptive factor 0..1 for a nudge type at an hour: ignored and dismissed ones fade out. */
export function adaptiveFactor(c: OutcomeCounts | undefined): number {
  if (!c) return 1;
  const n = c.answered + c.ignored + c.dismissed;
  if (n < ADAPT_MIN_SAMPLES) return 1;
  return Math.min(1, (c.answered + 0.5) / (c.answered + c.ignored + 2 * c.dismissed + 0.5));
}

const later = (label = "Później"): Chip => ({ label, action: { type: "later" } });

function nameVar(state: GameState): string {
  return state.settings.userName ? `, ${state.settings.userName}` : "";
}

export function reminderPrompt(state: GameState, now: number): PromptPlan | null {
  const due = state.reminders.filter((r) => r.status === "due").sort((a, b) => a.at - b.at)[0];
  if (!due) return null;
  const mute = activeMute(state.mute, now);
  const vars = { name: nameVar(state), text: due.text };
  return {
    kind: "reminder",
    nudgeType: null,
    text: mute ? pickLine("reminderShort", 0, vars) : pickLine("reminder", state.nudge.variant, vars),
    chips: [
      { label: "Zrobione", action: { type: "reminder_ack", id: due.id, outcome: "done" } },
      { label: "Za godzinę", action: { type: "reminder_ack", id: due.id, outcome: "hour" } },
      { label: "Jutro", action: { type: "reminder_ack", id: due.id, outcome: "tomorrow" } },
    ],
    listen: !mute,
    speak: mute?.kind !== "days",
    questId: null,
    reminderId: due.id,
  };
}

/**
 * Decide what (if anything) the companion should say on its own right now.
 * Pure: same input, same output. Call it every ~15 s on the device that has the companion.
 */
export function nextPrompt(input: SchedulerInput): PromptPlan | null {
  const { state, now, presence } = input;
  if (!input.hasCompanion || input.busy) return null;

  // Reminders the user asked for go through short mutes (spoken briefly) and days-mutes (bubble only).
  const reminder = reminderPrompt(state, now);
  if (reminder) return reminder;

  // Pocket profile: the companion stays quiet until the user starts talking.
  if (input.profile === "pocket") return null;
  if (activeMute(state.mute, now)) return null;
  if (now - state.nudge.lastPromptAt < MIN_PROMPT_GAP) return null;
  // The user just talked: no proactive line right after a conversation.
  const lastUser = state.messages.findLast((m) => m.role === "user");
  if (lastUser && now - lastUser.at < MIN_PROMPT_GAP) return null;

  const tz = state.settings.timezone;
  const today = dayKey(now, tz);
  const minutes = localMinutes(now, tz);
  const mode = effectiveMode(state, today);
  const s = state.settings;
  const n = state.nudge;
  const name = nameVar(state);
  const v = n.variant;

  if (n.returnPending && canStartScheduled(presence, now)) {
    return plan("return", pickLine("returnGreeting", v, { name }), [later()]);
  }

  // Sunday mirror from 19:00.
  const wd = zonedParts(now, tz).weekday;
  const sundayGameDay = wd === 0 && minutes >= 19 * 60;
  if (
    sundayGameDay &&
    n.lastWeeklyWeek !== weekKey(today) &&
    mode !== "sick" &&
    canStartScheduled(presence, now)
  ) {
    const from = addDays(today, -6);
    const items = state.recent.filter((a) => a.day >= from && a.day <= today);
    const by: Partial<Record<Area, number>> = {};
    for (const a of items) by[a.area] = (by[a.area] ?? 0) + 1;
    const top = (Object.entries(by) as [Area, number][]).sort((a, b) => b[1] - a[1])[0];
    const text = top
      ? pickLine("weekly", v, { name, count: items.length, area: AREA_NAMES_PL[top[0]] })
      : pickLine("weeklyQuiet", v, { name });
    const chips = [1, 2, 3, 4, 5].map((value) => ({
      label: String(value),
      action: { type: "rate_week", value } as const,
    }));
    return plan("weekly", text, chips);
  }

  // Evening debrief: from debriefFrom until the end of the game day (04:00).
  const debriefFrom = parseHm(s.debriefFrom) ?? 21 * 60;
  const evening = minutes >= debriefFrom || minutes < 4 * 60;
  if (evening && n.lastDebriefDay !== today && mode !== "sick" && canStartScheduled(presence, now)) {
    // What was on today's list: say what is already done, ask only about the rest.
    const logged = state.recent.filter((a) => a.day === today);
    const planned = state.quests.filter(
      (q) =>
        q.lastDoneDay === today || (q.status === "open" && (q.createdDay === today || q.dueDay === today)),
    );
    const isDone = (q: (typeof planned)[number]) =>
      q.lastDoneDay === today || logged.some((a) => a.questId === q.id || matchesQuest(q.title, a.text));
    const done = planned
      .filter(isDone)
      .map((q) => q.title)
      .slice(0, 3);
    const left = planned
      .filter((q) => q.status === "open" && !isDone(q))
      .map((q) => q.title)
      .slice(0, 3);
    const text =
      done.length && left.length
        ? pickLine("debriefDoneLeft", v, { name, done: joinPl(done), left: joinPl(left) })
        : done.length
          ? pickLine("debriefDone", v, { name, done: joinPl(done) })
          : left.length
            ? pickLine("debriefPlan", v, { name, list: joinPl(left) })
            : pickLine("debrief", v, { name });
    return plan("debrief", text, [later()]);
  }

  // Morning brief, first presence inside the window.
  const skipBrief = n.skipBriefDay === today;
  if (
    inWindow(minutes, s.briefFrom, s.briefUntil) &&
    n.lastBriefDay !== today &&
    !skipBrief &&
    canStartScheduled(presence, now)
  ) {
    const left = dueQuests(state.quests, today).map((q) => q.title);
    let text: string;
    if (mode === "sick") text = pickLine("briefSick", v, { name });
    else if (mode === "vacation") text = pickLine("briefVacation", v, { name });
    else if (mode === "crunch") text = pickLine("briefCrunch", v, { name });
    // Only mention rest points when there are some (a two-day break accrues none yet).
    else if (restedPool(state.stats, today) > 0)
      text = pickLine("briefRested", v, { name, rested: restedPool(state.stats, today) });
    else if (left.length) text = pickLine("briefLeftover", v, { name, list: joinPl(left.slice(0, 3)) });
    else text = pickLine("brief", v, { name });
    return plan("brief", text, [later()]);
  }

  // Daily nudges.
  if (mode === "sick" || mode === "vacation") return null;
  if (!inWindow(minutes, s.activeFrom, s.activeUntil)) return null;
  const count = n.day === today ? n.count : 0;
  if (count >= Math.min(3, s.maxNudgesPerDay)) return null;
  if (now - n.lastAt < NUDGE_GAP) return null;
  if (!canNudge(presence, now)) return null;
  return pickNudge(state, now, today, minutes, mode === "crunch");
}

function plan(kind: PromptKind, text: string, chips: Chip[], extra: Partial<PromptPlan> = {}): PromptPlan {
  return {
    kind,
    nudgeType: null,
    text,
    chips,
    listen: true,
    speak: true,
    questId: null,
    reminderId: null,
    ...extra,
  };
}

const CHORE_RE = /śmie|smiec|pranie|zmyw|odkurz|sprząt|sprzat|podlać|podlac|rachun/i;

function pickNudge(
  state: GameState,
  now: number,
  today: string,
  minutes: number,
  crunch: boolean,
): PromptPlan | null {
  const name = nameVar(state);
  const v = state.nudge.variant;
  const due = dueQuests(state.quests, today);
  const chore = due.find((q) => q.area === "home" && (q.repeatDays || CHORE_RE.test(q.title)));
  const quest = due.find((q) => q !== chore && (!crunch || q.size === "S"));
  const bodyToday = state.recent.some((a) => a.day === today && a.area === "body");

  const candidates: { type: NudgeType; make: () => PromptPlan }[] = [];
  if (chore)
    candidates.push({
      type: "chore",
      make: () =>
        plan("nudge", pickLine("chore", v, { name, quest: chore.title }), questChips(chore.id), {
          nudgeType: "chore",
          questId: chore.id,
        }),
    });
  if (quest)
    candidates.push({
      type: "quest",
      make: () =>
        plan("nudge", pickLine("quest", v, { name, quest: quest.title }), questChips(quest.id), {
          nudgeType: "quest",
          questId: quest.id,
        }),
    });
  if (!bodyToday && !crunch) {
    const variant = v % 3;
    const text = pickLine("challenge", variant, { name });
    const logText = ["10 pompek", "15 przysiadów", "krótkie rozciąganie"][variant]!;
    candidates.push({
      type: "challenge",
      make: () =>
        plan(
          "nudge",
          text,
          [
            { label: "Zrobione", action: { type: "log_challenge", area: "body", text: logText } },
            later("Nie teraz"),
          ],
          { nudgeType: "challenge" },
        ),
    });
  }
  candidates.push({
    type: "curiosity",
    make: () =>
      plan("nudge", pickLine("curiosity", v, { name }), [later("Nie teraz")], { nudgeType: "curiosity" }),
  });

  let best: { score: number; make: () => PromptPlan } | null = null;
  for (const c of candidates) {
    const factor = adaptiveFactor(state.nudge.stats[statKey(c.type, minutes)]);
    if (factor < ADAPT_SKIP_BELOW) continue;
    // Variety: the same type twice in a row only if nothing else fits.
    const variety = state.nudge.lastType === c.type ? 0.5 : 1;
    const score = BASE_PRIORITY[c.type] * factor * variety;
    if (!best || score > best.score) best = { score, make: c.make };
  }
  return best ? best.make() : null;
}

function questChips(id: string): Chip[] {
  return [
    { label: "Zrobione", action: { type: "complete_quest", id } },
    { label: "Jutro", action: { type: "postpone_quest", id } },
    { label: "Jeszcze nie", action: { type: "not_yet" } },
  ];
}
